import { useSelector, useDispatch } from 'react-redux';
import { RootState, AppDispatch } from '../store/store';
import { login, logout, verifyIpLogin, clearIpVerification } from '../store/authSlice';
import { LoginRequest } from '../types';

export const useAuth = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { 
    user, 
    isAuthenticated, 
    isLoading, 
    error,
    requiresIpVerification,
    ipVerificationToken,
    maskedEmail,
    pendingUserId,
    pendingUsername,
  } = useSelector((state: RootState) => state.auth);
  const isBusinessAccount =
    (user?.isBusinessAccount ?? false) ||
    (user?.accountTypeCode?.toUpperCase() === 'BUSINESS');

  const handleLogin = async (credentials: LoginRequest) => {
    return dispatch(login(credentials)).unwrap();
  };

  const handleLogout = async () => {
    return dispatch(logout()).unwrap();
  };

  const handleVerifyIpLogin = async (token: string, code: string) => {
    return dispatch(verifyIpLogin({ token, code })).unwrap();
  };

  const handleClearIpVerification = () => {
    dispatch(clearIpVerification());
  };

  return {
    user,
    isAuthenticated,
    isLoading,
    error,
    /** When true, enables advanced mailbox features in the UI. Set by admin only. */
    hasAdvancedMailbox: user?.hasAdvancedMailbox ?? false,
    login: handleLogin,
    logout: handleLogout,
    // IP Verification
    requiresIpVerification,
    ipVerificationToken,
    maskedEmail,
    pendingUserId,
    pendingUsername,
    verifyIpLogin: handleVerifyIpLogin,
    clearIpVerification: handleClearIpVerification,
    // Account Type Information
    /** Account type code (e.g., "CONSUMER", "BUSINESS", "ADVERTISER", etc.) */
    accountTypeCode: user?.accountTypeCode ?? 'CONSUMER',
    /** True if this is a credentialed business account */
    isBusinessAccount,
    /** True if user has active vendor credentials */
    hasVendorCredentials: user?.hasVendorCredentials ?? false,
    /** True if user has active marketer credentials */
    hasMarketerCredentials: user?.hasMarketerCredentials ?? false,
    /** True if user has active developer credentials */
    hasDeveloperCredentials: user?.hasDeveloperCredentials ?? false,
    /** Credential type if credentialed (VENDOR, MARKETER, DEVELOPER, etc.) */
    credentialType: user?.credentialType ?? null,
    /** True if this is any type of credentialed account */
    isCredentialedAccount: !!(user?.hasVendorCredentials || user?.hasMarketerCredentials || user?.hasDeveloperCredentials),
    /** True when logged in as a minor via a joint account alias */
    isMinor: user?.isMinor ?? false,
    /** Tab keys the minor is allowed to see; null/undefined uses platform defaults at runtime */
    minorAllowedTabs: user?.allowedTabs ?? null,
    /** True when logged in as a business-member alias (non-minor joint account context) */
    isJointContext: user?.isJointContext ?? false,
    /** The joint account this alias belongs to */
    jointAccountId: user?.jointAccountId ?? null,
    /** Name of the joint account (e.g. "QixitMarketing") */
    jointAccountName: user?.jointAccountName ?? null,
    /** The alias record ID */
    aliasId: user?.aliasId ?? null,
    /** Username typed at login (base account or premium alias). */
    loginHandle: user?.loginHandle ?? user?.username ?? null,
  };
};

